import {
  SHStack,
  SHTable,
  SHTableBody,
  SHTableCell,
  SHTableHead,
  SHTableRow,
} from "@components/design-systems";
import SHSkeleton from "@components/design-systems/sh-skeleton";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useTheme } from "@mui/system";

export const PortfolioSetupModalSkeleton = () => {
  const { palette } = useTheme();

  return (
    <SHStack height={"100%"}>
      <DialogTitle
        sx={{
          alignItems: "center",
          justifyItems: "center",
          height: "60px",
        }}
      >
        <SHSkeleton delay={false} height={30} width={"50%"} />
      </DialogTitle>

      <DialogContent
        sx={{
          height: "100%",
          alignItems: "center",
          justifyItems: "center",
          overflowY: "hidden",
        }}
      >
        {/* Dropdowns */}
        <SHStack height={"150px"} width={"100%"} gap={2}>
          <SHStack direction={"row"} width={"100%"} gap={3}>
            <SHStack width={"50%"} gap={1}>
              <SHSkeleton delay={false} height={20} width={"30%"} />
              <SHSkeleton delay={false} height={50} width={"100%"} />
            </SHStack>

            <SHStack width={"50%"} gap={1}>
              <SHSkeleton delay={false} height={20} width={"50%"} />
              <SHSkeleton delay={false} height={35} width={"100%"} />
            </SHStack>
          </SHStack>

          <SHStack direction={"row"} width={"100%"} gap={3}>
            <SHStack width={"50%"} gap={1}>
              <SHSkeleton delay={false} height={20} width={"50%"} />
              <SHSkeleton delay={false} height={35} width={"100%"} />
            </SHStack>

            <SHStack width={"50%"} gap={1}>
              <SHSkeleton delay={false} height={20} width={"50%"} />
              <SHSkeleton delay={false} height={35} width={"100%"} />
            </SHStack>
          </SHStack>
        </SHStack>

        {/* Portfolio Setup Items */}
        <SHStack height={"100%"} width={"100%"}>
          <SHStack marginTop={2} width={"100%"} overflow={"hidden"}>
            <SHTable>
              {/* Table Head */}
              <SHTableHead
                sx={{
                  backgroundColor: "white",
                }}
              >
                <SHTableRow>
                  <SHTableCell>
                    <SHStack paddingLeft={"84px"}>
                      <SHSkeleton delay={false} height={25} width={200} />
                    </SHStack>
                  </SHTableCell>

                  <SHTableCell>
                    <SHSkeleton delay={false} height={25} width={100} />
                  </SHTableCell>

                  <SHTableCell>
                    <SHSkeleton delay={false} height={25} width={100} />
                  </SHTableCell>
                </SHTableRow>
              </SHTableHead>

              {/* Table Body */}
              <SHTableBody
                sx={{
                  "&.MuiTableBody-root::before": {
                    lineHeight: "0px",
                    fontSize: "0px",
                  },
                  "& .AnalysisFeePortfolioSetup-table-row:nth-of-type(even)": {
                    backgroundColor: palette.secondary["50"],
                  },
                  "& .AnalysisFeePortfolioSetup-table-row:nth-of-type(odd)": {
                    backgroundColor: palette.common.white,
                  },
                }}
              >
                {Array.from({ length: 20 }).map((_, index) => (
                  <SHTableRow
                    className={"AnalysisFeePortfolioSetup-table-row"}
                    key={index}
                    sx={{
                      width: "100%",
                      height: 45,
                    }}
                  >
                    <SHTableCell>
                      <SHStack
                        direction={"row"}
                        alignContent={"center"}
                        alignItems={"center"}
                      >
                        <SHStack width={84}>
                          <SHStack
                            width={84}
                            direction={"row"}
                            gap={1}
                            display={index < 5 ? "none" : "flex"}
                          >
                            <SHSkeleton
                              variant={"circular"}
                              width={32}
                              height={32}
                              delay={false}
                            />
                            <SHSkeleton
                              variant={"circular"}
                              width={32}
                              height={32}
                              delay={false}
                            />
                          </SHStack>
                        </SHStack>

                        <SHSkeleton
                          delay={false}
                          height={25}
                          width={index % 2 === 0 ? 180 : 250}
                        />
                      </SHStack>
                    </SHTableCell>

                    <SHTableCell width={120}>
                      <SHSkeleton delay={false} height={25} width={100} />
                    </SHTableCell>

                    <SHTableCell width={220}>
                      <SHSkeleton delay={false} height={25} width={180} />
                    </SHTableCell>
                  </SHTableRow>
                ))}
              </SHTableBody>
            </SHTable>
          </SHStack>
        </SHStack>
      </DialogContent>

      <DialogActions
        sx={{
          width: "100%",
          alignItems: "center",
          justifyItems: "center",
          justifyContent: "center",
        }}
      >
        <SHStack gap={4} width={"100%"}>
          <SHStack
            width={"100%"}
            paddingTop={2}
            alignItems={"end"}
            justifyContent={"flex-end"}
            paddingRight={2}
          >
            <SHStack
              width={"600"}
              height={45}
              bgcolor={palette.secondary["50"]}
              direction={"row"}
            >
              <SHStack
                alignItems={"center"}
                justifyContent={"center"}
                paddingLeft={"15px"}
              >
                <SHSkeleton delay={false} height={25} width={140} />
              </SHStack>
              <SHStack
                width={190}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <SHSkeleton delay={false} height={25} width={100} />
              </SHStack>
              <SHStack
                width={218}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <SHSkeleton delay={false} height={25} width={120} />
              </SHStack>
            </SHStack>
          </SHStack>
          <SHStack
            width={"100%"}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"center"}
            paddingBottom={1}
            gap={2}
          >
            <SHSkeleton
              delay={false}
              height={35}
              width={150}
              sx={{
                borderRadius: "100px",
              }}
            />

            <SHSkeleton
              delay={false}
              height={35}
              width={150}
              sx={{
                borderRadius: "100px",
              }}
            />
          </SHStack>
        </SHStack>
      </DialogActions>
    </SHStack>
  );
};
